import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useApp } from "../contexts/AppContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  ChevronLeft, ChevronRight, Plus, Pencil, Trash2, CalendarDays,
} from "lucide-react";
import { MONTH_NAMES, formatCurrency, API_URL } from "../lib/utils";


const RECURRENCE_OPTIONS = [
  { value: "unica", label: "Unica" },
  { value: "mensile", label: "Mensile" },
  { value: "trimestrale", label: "Trimestrale" },
  { value: "semestrale", label: "Semestrale" },
  { value: "annuale", label: "Annuale" },
];

const RECURRENCE_LABELS = {
  unica: "Unica",
  mensile: "Mensile",
  trimestrale: "Trimestrale",
  semestrale: "Semestrale",
  annuale: "Annuale",
};

export default function ContractsPage() {
  const { companies, fetchCompanies } = useApp();
  const [commitments, setCommitments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterCompany, setFilterCompany] = useState("");

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", recurrence: "mensile", start_date: "", description: "", amount: "", company_id: "",
  });

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const loadCommitments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year, month };
      if (filterCompany) params.company_id = filterCompany;
      const res = await axios.get(`${API_URL}/api/commitments`, { params, withCredentials: true });
      setCommitments(res.data);
    } catch (err) {
      console.error("Error loading commitments:", err);
    } finally {
      setLoading(false);
    }
  }, [year, month, filterCompany]);

  useEffect(() => {
    loadCommitments();
  }, [loadCommitments]);

  const handlePrevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); } else { setMonth(month - 1); }
  };
  const handleNextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); } else { setMonth(month + 1); }
  };

  const openCreateDialog = () => {
    setEditing(null);
    setForm({
      name: "", recurrence: "mensile", start_date: "", description: "", amount: "",
      company_id: filterCompany || "",
    });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditing(item.id);
    setForm({
      name: item.name || "",
      recurrence: item.recurrence || "mensile",
      start_date: item.start_date || "",
      description: item.description || "",
      amount: item.amount || "",
      company_id: item.company_id || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const payload = {
      ...form,
      amount: form.amount ? parseFloat(form.amount) : null,
    };
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/commitments/${editing}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/commitments`, payload, { withCredentials: true });
      }
      setDialogOpen(false);
      loadCommitments();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo impegno e tutte le sue occorrenze?")) return;
    try {
      await axios.delete(`${API_URL}/api/commitments/${id}`, { withCredentials: true });
      loadCommitments();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleTogglePaid = async (item) => {
    try {
      await axios.put(`${API_URL}/api/commitments/${item.id}/occurrence`, {
        date: item.occurrence_date,
        is_paid: !item.is_paid,
        notes: item.payment_notes || "",
      }, { withCredentials: true });
      loadCommitments();
    } catch (err) {
      console.error("Error toggling paid:", err);
    }
  };

  const handleUpdateNotes = async (item, notes) => {
    try {
      await axios.put(`${API_URL}/api/commitments/${item.id}/occurrence`, {
        date: item.occurrence_date,
        is_paid: item.is_paid,
        notes,
      }, { withCredentials: true });
      loadCommitments();
    } catch (err) {
      console.error("Error updating notes:", err);
    }
  };

  const getCompanyName = (companyId) => {
    const c = companies.find(co => co.id === companyId);
    return c ? c.name : "-";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const parts = dateStr.split("-");
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
  };

  const totalAmount = commitments.reduce((sum, c) => sum + (c.amount || 0), 0);
  const totalPaid = commitments.filter(c => c.is_paid).reduce((sum, c) => sum + (c.amount || 0), 0);
  const totalUnpaid = totalAmount - totalPaid;

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-outfit tracking-tight text-[#09090B]">Scadenze e Impegni</h1>
          <p className="text-[#52525B] font-ibm-plex mt-1">Calendario impegni ricorrenti per azienda</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-black hover:bg-gray-800 text-white font-ibm-plex" data-testid="add-commitment-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuovo Impegno
        </Button>
      </div>

      {/* Filters */}
      <Card className="border border-[#E4E4E7]">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <Select value={filterCompany || "all"} onValueChange={(v) => setFilterCompany(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="commitment-filter-company" className="font-ibm-plex">
                <SelectValue placeholder="Tutte le aziende" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le aziende</SelectItem>
                {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handlePrevMonth} data-testid="commitment-prev-month">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex-1 text-center font-ibm-plex font-medium">
                {MONTH_NAMES[month]} {year}
              </div>
              <Button variant="outline" size="icon" onClick={handleNextMonth} data-testid="commitment-next-month">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex gap-4 text-sm font-ibm-plex">
              <div className="flex flex-col items-center px-3 py-2 bg-[#F4F4F5] rounded-md flex-1">
                <span className="text-[#52525B] text-xs">Totale</span>
                <span className="font-semibold text-[#09090B]">{formatCurrency(totalAmount)}</span>
              </div>
              <div className="flex flex-col items-center px-3 py-2 bg-green-50 rounded-md flex-1">
                <span className="text-green-600 text-xs">Pagato</span>
                <span className="font-semibold text-green-700">{formatCurrency(totalPaid)}</span>
              </div>
              <div className="flex flex-col items-center px-3 py-2 bg-red-50 rounded-md flex-1">
                <span className="text-red-600 text-xs">Da pagare</span>
                <span className="font-semibold text-red-700">{formatCurrency(totalUnpaid)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Commitments Table */}
      <Card className="border border-[#E4E4E7]">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-[#52525B] font-ibm-plex">Caricamento...</div>
          ) : commitments.length === 0 ? (
            <div className="py-12 text-center text-[#52525B] font-ibm-plex">
              <CalendarDays className="w-10 h-10 mx-auto mb-3 text-[#D4D4D8]" />
              <p>Nessun impegno per {MONTH_NAMES[month]} {year}</p>
              <p className="text-xs mt-1">Crea un nuovo impegno con il pulsante in alto</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F4F4F5]">
                    <TableHead className="font-ibm-plex font-semibold min-w-[180px]">Impegno</TableHead>
                    <TableHead className="font-ibm-plex font-semibold min-w-[100px]">Ricorrenza</TableHead>
                    <TableHead className="font-ibm-plex font-semibold min-w-[100px]">Data</TableHead>
                    <TableHead className="font-ibm-plex font-semibold min-w-[120px]">Azienda</TableHead>
                    <TableHead className="font-ibm-plex font-semibold min-w-[180px]">Descrizione</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-right min-w-[100px]">Importo</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-center min-w-[70px]">Pagato</TableHead>
                    <TableHead className="font-ibm-plex font-semibold min-w-[180px]">Note Pagamento</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-center min-w-[80px]">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commitments.map((item, idx) => (
                    <TableRow
                      key={`${item.id}-${item.occurrence_date}-${idx}`}
                      className={item.is_paid ? "bg-green-50/50" : ""}
                      data-testid={`commitment-row-${item.id}`}
                    >
                      <TableCell className="font-ibm-plex font-medium">{item.name}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-ibm-plex font-medium bg-[#F4F4F5] text-[#52525B]">
                          {RECURRENCE_LABELS[item.recurrence] || item.recurrence}
                        </span>
                      </TableCell>
                      <TableCell className="font-ibm-plex-mono text-sm">{formatDate(item.occurrence_date)}</TableCell>
                      <TableCell className="font-ibm-plex text-sm">{getCompanyName(item.company_id)}</TableCell>
                      <TableCell className="font-ibm-plex text-sm text-[#52525B] max-w-[200px] truncate">{item.description || "-"}</TableCell>
                      <TableCell className="font-ibm-plex-mono text-sm text-right font-medium">
                        {item.amount ? formatCurrency(item.amount) : "-"}
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={item.is_paid}
                          onCheckedChange={() => handleTogglePaid(item)}
                          data-testid={`commitment-paid-${item.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          className="font-ibm-plex text-sm h-8 border-[#E4E4E7]"
                          placeholder="Note..."
                          defaultValue={item.payment_notes || ""}
                          onBlur={(e) => {
                            if (e.target.value !== (item.payment_notes || "")) {
                              handleUpdateNotes(item, e.target.value);
                            }
                          }}
                          data-testid={`commitment-notes-${item.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditDialog(item)} data-testid={`edit-commitment-${item.id}`}>
                            <Pencil className="w-3.5 h-3.5 text-[#52525B]" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDelete(item.id)} data-testid={`delete-commitment-${item.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">{editing ? "Modifica Impegno" : "Nuovo Impegno"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <label className="text-xs font-ibm-plex font-medium">Nome Impegno *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Es. Affitto Hotel Belsito"
                className="font-ibm-plex"
                data-testid="commitment-form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-ibm-plex font-medium">Ricorrenza *</label>
                <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                  <SelectTrigger className="font-ibm-plex" data-testid="commitment-form-recurrence">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECURRENCE_OPTIONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-ibm-plex font-medium">Data Inizio *</label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="font-ibm-plex"
                  data-testid="commitment-form-date"
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-ibm-plex font-medium">Azienda</label>
              <Select value={form.company_id || "none"} onValueChange={(v) => setForm({ ...form, company_id: v === "none" ? "" : v })}>
                <SelectTrigger className="font-ibm-plex" data-testid="commitment-form-company">
                  <SelectValue placeholder="Seleziona azienda" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna azienda</SelectItem>
                  {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-ibm-plex font-medium">Descrizione</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Descrizione dell'impegno..."
                className="font-ibm-plex"
                rows={2}
                data-testid="commitment-form-description"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-ibm-plex font-medium">Importo</label>
              <Input
                type="number"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
                className="font-ibm-plex"
                data-testid="commitment-form-amount"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="font-ibm-plex">Annulla</Button>
            <Button
              onClick={handleSave}
              disabled={!form.name || !form.start_date}
              className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
              data-testid="commitment-form-save"
            >
              {editing ? "Salva" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
